/**
 * 双星核的图层（P3b，design §11.12）：中心光晕里的两颗小星——金（先加入的人）、
 * 玫瑰（后加入的）。加法混合的 glow Sprite，同中心光晕 core 的做法，在盘面 y = 0 上
 * 对径转；并轨时玫瑰身后拖一串渐淡的残影（连成光尾）。
 * 轨迹是 twin.ts 的纯函数、模式和「来过」在 galaxy.ts——这里只管把星摆在给定的位置、
 * 按给定的不透明度和尺寸画，不放公式。
 */
import * as THREE from 'three';
import type { Vec3 } from '@star-sea/shared';

/**
 * 残影个数：位置由 galaxy 按过去 i·TWIN_GHOST_STEP 秒的轨迹给（twin.ts 的 launchTrack），i 从 1 起。
 * 要密：飞入段每秒走半个屏幕高，隔得稀了（原来 8 个、隔 0.035 秒）连不成光尾，像一串珠子
 */
export const TWIN_GHOSTS = 16;
/** 残影渐淡：不透明度 ×(1 − i/N)²、尺寸 ×(1 − 0.035i)，尾巴越往后越细 */
const GHOST_FADE = (i: number) => (1 - i / TWIN_GHOSTS) ** 2;
const GHOST_SHRINK = (i: number) => 1 - 0.035 * i;

export interface TwinDraw {
  x: number;
  y: number;
  z: number;
  opacity: number;
  size: number;
  /** 颜色乘的倍数（「来过」时 > 1）：画在半精度缓冲里，超过 1 是真的更亮、辉光也更明显；不透明度到 1 就截顶了 */
  gain?: number;
}

export class TwinCore {
  private readonly group = new THREE.Group();
  private readonly gold: THREE.Sprite;
  private readonly rose: THREE.Sprite;
  private readonly ghosts: THREE.Sprite[] = [];
  private readonly goldColor: THREE.Color;
  private readonly roseColor: THREE.Color;

  constructor(scene: THREE.Object3D, textures: { glow: THREE.Texture; gold: THREE.Color; rose: THREE.Color }) {
    const sprite = (color: THREE.Color): THREE.Sprite => {
      // 同中心光晕：glow 贴图、加法混合、不写深度
      const s = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: textures.glow, color, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0 }),
      );
      s.visible = false;
      s.renderOrder = 2;
      this.group.add(s);
      return s;
    };
    this.goldColor = textures.gold.clone();
    this.roseColor = textures.rose.clone();
    this.gold = sprite(textures.gold);
    this.rose = sprite(textures.rose);
    for (let i = 0; i < TWIN_GHOSTS; i++) this.ghosts.push(sprite(textures.rose));
    scene.add(this.group);
  }

  /**
   * 每帧：null 的星不画；ghosts 是玫瑰过去的位置（新→旧，ghostCount 个有效），
   * 第 i 个（从 1 起）不透明度 ×(1−i/N)²、尺寸 ×(1−0.035i)；锁定后 ghostCount = 0 收掉。
   */
  update(o: {
    gold: TwinDraw | null;
    rose: TwinDraw | null;
    ghosts: readonly Vec3[];
    ghostCount: number;
    ghostOpacity: number;
    ghostSize: number;
  }): void {
    const put = (s: THREE.Sprite, p: TwinDraw | null, base: THREE.Color): void => {
      if (!p || p.opacity <= 0.002) {
        s.visible = false;
        return;
      }
      s.visible = true;
      s.position.set(p.x, p.y, p.z);
      s.scale.setScalar(p.size);
      const m = s.material as THREE.SpriteMaterial;
      m.opacity = Math.min(1, p.opacity);
      m.color.copy(base).multiplyScalar(p.gain ?? 1);
    };
    put(this.gold, o.gold, this.goldColor);
    put(this.rose, o.rose, this.roseColor);
    for (let i = 1; i <= TWIN_GHOSTS; i++) {
      const s = this.ghosts[i - 1]!,
        g = i <= o.ghostCount ? o.ghosts[i - 1] : undefined;
      if (!g || o.ghostOpacity <= 0.002) {
        s.visible = false;
        continue;
      }
      s.visible = true;
      s.position.set(g.x, g.y, g.z);
      s.scale.setScalar(o.ghostSize * GHOST_SHRINK(i));
      (s.material as THREE.SpriteMaterial).opacity = Math.min(1, o.ghostOpacity * GHOST_FADE(i));
    }
  }
}
